import { z } from 'zod';

/**
 * What is running on a stream right now that its chat cannot show: a hype
 * train, a poll, a prediction.
 *
 * None of these reach the anonymous chat connection a room reads. They are
 * EventSub topics behind scopes only the broadcaster grants, so a room shows
 * them for a CONNECTED channel and for no other (packages/chat/src/events.ts).
 *
 * Live only, like the mod feed: the realtime service holds the current state
 * of each in memory and nothing is ever stored. Every item is a whole
 * snapshot, never a delta, so a viewer who arrives mid-train is as current as
 * one who watched it start.
 *
 * Keyed by the channel's Twitch USER ID. A room knows it from Twitch's own
 * `room-id` tag, every one of these payloads carries it, and — unlike the
 * login — it survives a rename. The login rides along for display, as the
 * channel is called in the event that produced the item.
 *
 *   running   collecting: contributions, votes, predictions
 *   locked    a prediction that stopped taking predictions, not yet resolved
 *   ended     over; kept briefly so the room can say how it finished
 */
export const HAPPENING_STATES = ['running', 'locked', 'ended'] as const;

const Base = z.object({
  /** Twitch's id for this train, poll or prediction. */
  id: z.string(),
  /** The channel's Twitch user id: the key. */
  broadcaster_id: z.string(),
  /** The channel's Twitch login as of this event, lower case. Display only. */
  login: z.string(),
  state: z.enum(HAPPENING_STATES),
  started_at: z.string().datetime(),
  /** Null until it is over. */
  ended_at: z.string().datetime().nullable(),
});

const Contributor = z.object({
  login: z.string(),
  name: z.string(),
  /** `bits`, `subscription` or `other`, as Twitch says it. */
  type: z.string(),
  total: z.number(),
});

export const HappeningHypeTrain = Base.extend({
  kind: z.literal('hype_train'),
  level: z.number().int(),
  /** Points toward the current level, out of `goal`. */
  progress: z.number(),
  goal: z.number(),
  total: z.number(),
  /** When it runs out unless somebody contributes. Null once it has ended. */
  expires_at: z.string().datetime().nullable(),
  /** `regular`, `treasure` or `golden_kappa`. */
  train_type: z.string(),
  shared: z.boolean(),
  top: z.array(Contributor),
});

export const HappeningPoll = Base.extend({
  kind: z.literal('poll'),
  title: z.string(),
  choices: z.array(z.object({ id: z.string(), title: z.string(), votes: z.number() })),
  ends_at: z.string().datetime().nullable(),
  /** `completed` or `terminated` (ended early). Null while it runs. */
  result: z.string().nullable(),
});

export const HappeningPrediction = Base.extend({
  kind: z.literal('prediction'),
  title: z.string(),
  outcomes: z.array(z.object({
    id: z.string(),
    title: z.string(),
    /** `blue` or `pink`, as Twitch colours them. */
    color: z.string(),
    users: z.number(),
    points: z.number(),
  })),
  locks_at: z.string().datetime().nullable(),
  /** Null until resolved, and null for a canceled one. */
  winning_outcome_id: z.string().nullable(),
  /** `resolved` or `canceled`. Null while it runs. */
  result: z.string().nullable(),
});

export const Happening = z.discriminatedUnion('kind', [HappeningHypeTrain, HappeningPoll, HappeningPrediction]);
export type Happening = z.infer<typeof Happening>;
export type HappeningHypeTrain = z.infer<typeof HappeningHypeTrain>;
export type HappeningPoll = z.infer<typeof HappeningPoll>;
export type HappeningPrediction = z.infer<typeof HappeningPrediction>;

/** GET /v1/happenings?logins=a,b */
export const HappeningsResponse = z.object({
  /**
   * Which of the asked channel ids tomo receives these events for: connected, with
   * at least one of the scopes granted. A room whose channels are none of
   * them has nothing to wait for, and asks far less often.
   */
  watching: z.array(z.string()),
  data: z.array(Happening),
});
export type HappeningsResponse = z.infer<typeof HappeningsResponse>;
